import React, { useState, useMemo } from "react";
import CreatePublish from "./CreatePublish";
import PreUpload from "./PreUpload";
import PreMcq from "./PreMcq";
import DraftPublish from "./DraftPublish";
import OperationType from "../../Enum/OperationTypeEnum";
import ComponentType from "../../Enum/ComponentTypeEnum";
import axios from "../../axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ListChecks, Award, Timer, AlertTriangle, Info } from "lucide-react";
import { useSelector } from "react-redux";

const ACCENT = "#F28C28";
const ACCENT_SOFT = "#FFF7ED";
const BORDER = "#E5E7EB";
const TEXT_PRIMARY = "#111827";
const TEXT_SECONDARY = "#6B7280";
const BG_SOFT = "#F9FAFB";

// ==========================================
// Helper: Stat Card
// ==========================================
const StatCard = ({ icon: Icon, label, value, sub, accent = false }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: "12px",
      border: `1px solid ${accent ? "#FCD34D" : BORDER}`,
      background: accent ? ACCENT_SOFT : "#fff",
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      height: "100%",
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "10px",
        background: accent ? "#FDE68A" : BG_SOFT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon size={18} color={accent ? "#92400E" : ACCENT} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          color: TEXT_SECONDARY,
          letterSpacing: "0.4px",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "18px",
          fontWeight: 700,
          color: TEXT_PRIMARY,
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: "11px", color: TEXT_SECONDARY }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Paper>
);

// ==========================================
// Helper: Distribution Bar Row
// ==========================================
const DistRow = ({ label, marks, count, maxMarks, color }) => {
  const percent = maxMarks > 0 ? (marks / maxMarks) * 100 : 0;
  return (
    <div className="d-flex align-items-center gap-3 mb-2">
      <Typography
        sx={{
          width: "130px",
          fontSize: "13px",
          fontWeight: 500,
          color: "#4B5563",
          flexShrink: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={label}
      >
        {label}
      </Typography>
      <div
        style={{
          flex: 1,
          height: "10px",
          backgroundColor: "#E5E7EB",
          borderRadius: "5px",
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            backgroundColor: color,
            borderRadius: "5px",
            transition: "width 0.3s ease",
          }}
        ></div>
      </div>
      <Typography
        sx={{
          width: "100px",
          fontSize: "13px",
          fontWeight: 600,
          color: "#4B5563",
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        {marks} marks ({count}Q)
      </Typography>
    </div>
  );
};

const QuestionListAssessment = ({
  change,
  questions,
  setQuestions,
  assessmentDetails,
  setAssessmentDetails,
  operationType,
  total_marks,
  componentType,
  name,
  redirectStateData,
  isChecked,
  selectedBox,
}) => {
  const navigate = useNavigate();
  const [click, setClick] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showDraftProcessing, setShowDraftProcessing] = useState(false);

  // ==========================================
  // Redux data for charts
  // ==========================================
  const difficultyData = useSelector(
    (state) => state.initialData.difficultyLevels,
  );
  const skillsData = useSelector((state) => state.initialData.skills);
  const questionTypesData = useSelector(
    (state) => state.initialData.questionTypes,
  );

  // ==========================================
  // Session ids
  // ==========================================
  const academicSessionId = JSON.parse(
    localStorage.getItem("academicYearSessionId"),
  );
  const timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );

  // ==========================================
  // Basic stats
  // ==========================================
  const maxMarks = Number(assessmentDetails?.maxMarks) || 0;
  const isMarksMismatch = maxMarks > 0 && Number(total_marks) !== maxMarks;
  const marksDiff = Number(total_marks) - maxMarks;

  const incompleteCount = useMemo(() => {
    return (questions || []).filter((q) => {
      const isMissingBasic =
        !q.title ||
        !q.skill_id ||
        !q.type_id ||
        !q.chapter_id ||
        !q.correct_score ||
        !q.difficulty_level;
      let isMissingOptions = false;
      if (q.type_id) {
        if (Array.isArray(q.options) && q.options.length > 0)
          isMissingOptions = q.options.some((opt) => !opt?.key || !opt?.value);
        if (Array.isArray(q.correct_answers))
          isMissingOptions =
            isMissingOptions ||
            q.correct_answers.some(
              (ans) =>
                q?.options?.length > 0 &&
                (!ans?.option_key || ans.option_key.trim() === ""),
            );
      }
      return isMissingBasic || isMissingOptions;
    }).length;
  }, [questions]);

  // ==========================================
  // Distribution stats
  // ==========================================
  const { difficultyStats, skillStats, typeStats } = useMemo(() => {
    const diff = {};
    const skill = {};
    const type = {};

    (questions || []).forEach((q) => {
      const marks = Number(q.correct_score) || 0;

      const diffId = q.difficulty_level || "unknown";
      if (!diff[diffId]) diff[diffId] = { marks: 0, count: 0 };
      diff[diffId].marks += marks;
      diff[diffId].count += 1;

      const skillId = q.skill_id || "unknown";
      if (!skill[skillId]) skill[skillId] = { marks: 0, count: 0 };
      skill[skillId].marks += marks;
      skill[skillId].count += 1;

      const typeId = q.type_id || "unknown";
      if (!type[typeId]) type[typeId] = { marks: 0, count: 0 };
      type[typeId].marks += marks;
      type[typeId].count += 1;
    });

    return {
      difficultyStats: diff,
      skillStats: skill,
      typeStats: type,
    };
  }, [questions]);

  // ==========================================
  // 👈 BUILD CLEANED QUESTIONS (same as CreateQuestion)
  // ==========================================
  const buildCleanedQuestions = (rawQuestions) => {
    return (rawQuestions || []).map((val) => {
      const q = { ...val };
      if (q.is_question_edited || q.id !== undefined) delete q.id;
      const isManuallyGraded = q.question_type === "true";
      const isQuestionBank = componentType === ComponentType.QUESTION_BANK;

      if (isQuestionBank) {
        if (isManuallyGraded)
          q.subjective_evaluation = {
            enabled: false,
            expectedAnswer: "",
            rubric: [],
          };
        else delete q.subjective_evaluation;
        return q;
      }

      if (
        !(
          selectedBox === "auto-evaluate" ||
          selectedBox?.autoEvaluate === true ||
          selectedBox?.isAutoEvaluate === true
        )
      ) {
        delete q.subjective_evaluation;
        return q;
      }
      if (!isManuallyGraded) {
        delete q.subjective_evaluation;
        return q;
      }

      const se = q.subjective_evaluation;
      const hasContent =
        (se?.expectedAnswer && se.expectedAnswer.trim()) ||
        (Array.isArray(se?.rubric) && se.rubric.length > 0);
      if (!hasContent) {
        delete q.subjective_evaluation;
        return q;
      }

      const isAiEnabled = se?.enabled ?? false;
      let finalRubric = [];
      if (isAiEnabled)
        finalRubric = (se.rubric || []).filter(
          (r) => (r.criterion && r.criterion.trim()) || Number(r.maxMarks) > 0,
        );
      q.subjective_evaluation = {
        enabled: isAiEnabled,
        expectedAnswer: se.expectedAnswer || "",
        rubric: finalRubric,
      };
      return q;
    });
  };

  // ==========================================
  // 👈 DIRECT SAVE AS DRAFT (no UX modal)
  // ==========================================
  const handleSaveAsDraft = async () => {
    if (isSavingDraft) return;

    if (!questions || questions.length === 0) {
      toast.error("Please add at least one question before saving.");
      return;
    }

    setIsSavingDraft(true);

    const isHeavy = questions.length > 20;
    if (isHeavy) setShowDraftProcessing(true);

    const cleanedQuestions = buildCleanedQuestions(questions);

    // 👈 Existing draft id (agar UPDATE flow hai)
    const existingId = assessmentDetails?.id || null;
    const isUpdate = Boolean(existingId);

    const payload = {
      // 👈 id sirf tab bhejo jab existing ho
      ...(existingId ? { id: existingId } : {}),
      name: assessmentDetails?.name,
      description: assessmentDetails?.description,
      type_id:
        componentType === ComponentType.ASSESSMENT
          ? null
          : assessmentDetails?.type_id,
      assessmentTypeId: assessmentDetails?.assessmentTypeId,
      assessmentEventId: assessmentDetails?.assessmentEventId,
      ...(componentType === ComponentType.HOMEWORK
        ? {
            academic_session_id: academicSessionId,
            timetable_session_id: timeTableSessionId,
          }
        : {
            academicSessionId: academicSessionId,
            timetableSessionId: timeTableSessionId,
          }),
      standard_id: assessmentDetails?.standard_id?.id,
      subject_id: assessmentDetails?.subject_id?.id,
      teacher_id: assessmentDetails?.teacher_id,
      section_id: assessmentDetails?.section_id?.id,
      tags: assessmentDetails?.tags || [],
      status: "draft", // 👈 hamesha draft
      isAdaptive:
        selectedBox === "adaptive" ||
        selectedBox?.adaptive ||
        selectedBox?.isAdaptive
          ? true
          : false,
      autoEvalEnabled:
        selectedBox === "auto-evaluate" || selectedBox?.autoEvaluate
          ? true
          : false,
      total_duration: assessmentDetails?.total_duration,
      // existing draft ke liye schedule fields bhi bhej do (optional)
      ...(isUpdate
        ? {
            deadline: assessmentDetails?.deadline,
            schedule_date: assessmentDetails?.schedule_date,
            schedule_time: assessmentDetails?.schedule_time,
            add_to_live_session: assessmentDetails?.add_to_live_session,
          }
        : {}),
      questions: cleanedQuestions,
    };

    try {
      let res = null;

      // ==========================================
      // ASSESSMENT
      // ==========================================
      if (componentType === ComponentType.ASSESSMENT) {
        if (isUpdate) {
          // 👈 existing draft → UPDATE
          res = await axios.put(
            `/api/v1/assessment/update/${existingId}`,
            payload,
          );
        } else {
          // 👈 naya draft → CREATE
          res = await axios.post(`/api/v1/assessment/create`, payload);
        }

        if (res) {
          if (
            redirectStateData !== null &&
            redirectStateData?.from === "liveSession"
          )
            navigate("/liveSession", { state: redirectStateData });
          else navigate("/publishedAssessment", { selectedBox: selectedBox });
        }
      }

      // ==========================================
      // HOMEWORK
      // ==========================================
      else if (componentType === ComponentType.HOMEWORK) {
        if (isUpdate) {
          // 👈 existing draft → UPDATE
          res = await axios.put(
            `/api/v1/homework/update/${existingId}`,
            payload,
          );
        } else {
          // 👈 naya draft → CREATE
          res = await axios.post(`/api/v1/homework/create`, payload);
        }

        if (res) {
          if (
            redirectStateData !== null &&
            redirectStateData?.from === "liveSession"
          )
            navigate("/liveSession", { state: redirectStateData });
          else navigate("/PublishedHomework");
        }
      }
    } catch (error) {
      console.log("Draft save error:", error);
      toast.error("Failed to save draft. Please try again.");
    } finally {
      setIsSavingDraft(false);
      setShowDraftProcessing(false);
      if (componentType === ComponentType.ASSESSMENT) {
        localStorage.removeItem("temp_Questions_ASSESSMENT");
        localStorage.removeItem("currentStep");
        localStorage.removeItem("assessmentDetails");
        localStorage.removeItem("selectedBox");
      }
      if (componentType === ComponentType.HOMEWORK) {
        localStorage.removeItem("temp_Questions_HOMEWORK");
        localStorage.removeItem("currentStepHW");
        localStorage.removeItem("HomeWorkDetails");
      }
    }
  };

  return (
    <>
      {!click && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
            p: 2,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 0,
              borderRadius: "16px",
              background: BG_SOFT,
              border: `1px solid ${BORDER}`,
              position: "relative",
              width: { xs: "96%", sm: "92%", md: "88%", lg: "1200px" },
              maxWidth: "1200px",
              maxHeight: "95vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <IconButton
              onClick={() => change(false)}
              size="small"
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: TEXT_SECONDARY,
                background: "#fff",
                border: `1px solid ${BORDER}`,
                zIndex: 10,
                "&:hover": { background: ACCENT_SOFT, color: ACCENT },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* HEADER */}
            <Box
              sx={{ p: "24px 32px 16px", background: BG_SOFT, flexShrink: 0 }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: TEXT_PRIMARY,
                  mb: 1,
                  letterSpacing: "-0.5px",
                }}
              >
                Review & Publish
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: TEXT_SECONDARY }}>
                  <b style={{ color: TEXT_PRIMARY }}>{name || "Assessment"}</b>{" "}
                  · Review questions, marks distribution and validation before
                  publishing.
                </Typography>

                {isMarksMismatch || incompleteCount > 0 ? (
                  <Chip
                    icon={
                      <WarningAmberIcon
                        fontSize="small"
                        style={{ color: "#92400E" }}
                      />
                    }
                    label={
                      isMarksMismatch
                        ? `Marks mismatch (${total_marks}/${maxMarks})`
                        : `${incompleteCount} incomplete`
                    }
                    size="small"
                    sx={{
                      backgroundColor: "#FEF3C7",
                      color: "#92400E",
                      fontWeight: 600,
                      fontSize: "12px",
                      px: 1,
                    }}
                  />
                ) : (
                  <Chip
                    icon={
                      <CheckCircleIcon
                        fontSize="small"
                        style={{ color: "#065F46" }}
                      />
                    }
                    label="Ready to publish"
                    size="small"
                    sx={{
                      backgroundColor: "#D1FAE5",
                      color: "#065F46",
                      fontWeight: 600,
                      fontSize: "12px",
                      px: 1,
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* SCROLLABLE CONTENT */}
            <Box
              sx={{
                overflowY: "auto",
                px: "32px",
                pb: "24px",
                flex: 1,
                minHeight: 0,
                background: BG_SOFT,
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "10px",
                },
              }}
            >
              {/* Stat Cards */}
              <div className="row g-3 mb-4 mt-1">
                <div className="col-md-3">
                  <StatCard
                    icon={ListChecks}
                    label="Questions"
                    value={questions?.length || 0}
                  />
                </div>
                <div className="col-md-3">
                  <StatCard
                    icon={Award}
                    label="Total Marks"
                    value={
                      maxMarks > 0
                        ? `${total_marks} / ${maxMarks}`
                        : total_marks
                    }
                    accent={isMarksMismatch}
                  />
                </div>
                <div className="col-md-3">
                  <StatCard
                    icon={Timer}
                    label="Duration"
                    value={`${assessmentDetails?.total_duration || 0} min`}
                  />
                </div>
                <div className="col-md-3">
                  <StatCard
                    icon={incompleteCount > 0 ? AlertTriangle : CheckCircleIcon}
                    label="Incomplete"
                    value={incompleteCount}
                    accent={incompleteCount > 0}
                  />
                </div>
              </div>

              {/* Banners */}
              {isMarksMismatch && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "#FEF3C7",
                    border: "1px solid #FCD34D",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    fontSize: "13px",
                    color: "#92400E",
                    mb: 2,
                  }}
                >
                  <AlertTriangle size={16} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                    {marksDiff > 0
                      ? `Total marks exceeded by ${marksDiff}. Please reduce ${marksDiff} mark(s).`
                      : `${Math.abs(marksDiff)} mark(s) remaining. Please add more questions.`}
                  </Typography>
                </Box>
              )}
              {incompleteCount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "#FEE2E2",
                    border: "1px solid #FCA5A5",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    fontSize: "13px",
                    color: "#991B1B",
                    mb: 2,
                  }}
                >
                  <AlertTriangle size={16} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                    {incompleteCount} question(s) need attention. Complete them
                    before publishing.
                  </Typography>
                </Box>
              )}
              {!isMarksMismatch && incompleteCount === 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    backgroundColor: "#EFF6FF",
                    border: "1px solid #BFDBFE",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    fontSize: "13px",
                    color: "#1E40AF",
                    mb: 2,
                  }}
                >
                  <Info size={16} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                    All questions pass validation. Review content accuracy
                    before finalizing.
                  </Typography>
                </Box>
              )}

              {/* Distribution Charts */}
              <div className="row g-4 mb-4">
                <div className="col-md-6">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                      background: "#fff",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        mb: 2,
                      }}
                    >
                      Difficulty{" "}
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                          color: TEXT_SECONDARY,
                        }}
                      >
                        (marks allocated)
                      </span>
                    </Typography>
                    <div className="d-flex flex-column gap-2">
                      {difficultyData?.map((level, index) => {
                        const stats = difficultyStats[level.id] || {
                          marks: 0,
                          count: 0,
                        };
                        return (
                          <DistRow
                            key={index}
                            label={level.name}
                            marks={stats.marks}
                            count={stats.count}
                            maxMarks={total_marks}
                            color="#10B981"
                          />
                        );
                      })}
                      {difficultyStats["unknown"] && (
                        <DistRow
                          label="Unclassified"
                          marks={difficultyStats["unknown"].marks}
                          count={difficultyStats["unknown"].count}
                          maxMarks={total_marks}
                          color="#9CA3AF"
                        />
                      )}
                    </div>
                  </Paper>
                </div>

                <div className="col-md-6">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                      background: "#fff",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        mb: 2,
                      }}
                    >
                      Bloom's Skills{" "}
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                          color: TEXT_SECONDARY,
                        }}
                      >
                        (marks allocated)
                      </span>
                    </Typography>
                    <div className="d-flex flex-column gap-2">
                      {skillsData?.map((skill, index) => {
                        const stats = skillStats[skill.id] || {
                          marks: 0,
                          count: 0,
                        };
                        return (
                          <DistRow
                            key={index}
                            label={skill.name}
                            marks={stats.marks}
                            count={stats.count}
                            maxMarks={total_marks}
                            color="#6366F1"
                          />
                        );
                      })}
                      {skillStats["unknown"] && (
                        <DistRow
                          label="Unclassified"
                          marks={skillStats["unknown"].marks}
                          count={skillStats["unknown"].count}
                          maxMarks={total_marks}
                          color="#9CA3AF"
                        />
                      )}
                    </div>
                  </Paper>
                </div>

                <div className="col-md-6">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                      background: "#fff",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        mb: 2,
                      }}
                    >
                      Question Types{" "}
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                          color: TEXT_SECONDARY,
                        }}
                      >
                        (marks allocated)
                      </span>
                    </Typography>
                    <div className="d-flex flex-column gap-2">
                      {questionTypesData?.map((type, index) => {
                        const stats = typeStats[type.id] || {
                          marks: 0,
                          count: 0,
                        };
                        return (
                          <DistRow
                            key={index}
                            label={type.name}
                            marks={stats.marks}
                            count={stats.count}
                            maxMarks={total_marks}
                            color="#F59E0B"
                          />
                        );
                      })}
                      {typeStats["unknown"] && (
                        <DistRow
                          label="Unclassified"
                          marks={typeStats["unknown"].marks}
                          count={typeStats["unknown"].count}
                          maxMarks={total_marks}
                          color="#9CA3AF"
                        />
                      )}
                    </div>
                  </Paper>
                </div>

                <div className="col-md-6">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      border: `1px dashed ${BORDER}`,
                      background: "#fff",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: TEXT_SECONDARY,
                        textAlign: "center",
                      }}
                    >
                      Review distribution summary
                      <br />
                      <span style={{ fontSize: "12px" }}>
                        (Difficulty, Skills & Types shown above)
                      </span>
                    </Typography>
                  </Paper>
                </div>
              </div>

              {/* Question list */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  border: `1px solid ${BORDER}`,
                  background: "#fff",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: TEXT_PRIMARY,
                    mb: 2,
                  }}
                >
                  Question-by-question review
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <div className="d-flex flex-column gap-3">
                  {questions?.length > 0 ? (
                    questions.map((question, index) =>
                      question.question_type === "false" ? (
                        <PreMcq
                          key={index}
                          nextQuestion={index}
                          question={question}
                        />
                      ) : (
                        <PreUpload
                          key={index}
                          nextQuestion={index}
                          question={question}
                          selectedBox={selectedBox}
                        />
                      ),
                    )
                  ) : (
                    <Typography
                      align="center"
                      color="text.secondary"
                      sx={{ py: 4 }}
                    >
                      No questions to review.
                    </Typography>
                  )}
                </div>
              </Paper>
            </Box>

            {/* STICKY FOOTER */}
            <Box
              sx={{
                p: "16px 32px",
                borderTop: `1px solid ${BORDER}`,
                background: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: TEXT_SECONDARY,
                }}
              >
                Auto-saved locally
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {/* Edit */}
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => change(false)}
                  sx={{
                    borderColor: "#D1D5DB",
                    color: TEXT_PRIMARY,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 3,
                    "&:hover": {
                      borderColor: "#9CA3AF",
                      background: BG_SOFT,
                    },
                  }}
                >
                  Edit Questions
                </Button>

                {/* 👈 Save as Draft — DIRECT API CALL */}
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  disabled={
                    !questions || questions.length === 0 || isSavingDraft
                  }
                  onClick={handleSaveAsDraft}
                  sx={{
                    borderColor: ACCENT,
                    color: ACCENT,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 3,
                    "&:hover": {
                      borderColor: "#e07c24",
                      background: ACCENT_SOFT,
                    },
                    "&.Mui-disabled": {
                      borderColor: "#E5E7EB",
                      color: "#9CA3AF",
                    },
                  }}
                >
                  {isSavingDraft ? "Saving..." : "Save as Draft"}
                </Button>

                {/* Publish */}
                <Button
                  variant="contained"
                  startIcon={<RocketLaunchIcon />}
                  disabled={
                    !questions || questions.length === 0 || isMarksMismatch
                  }
                  onClick={() => setClick(true)}
                  sx={{
                    background: ACCENT,
                    color: "#fff",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 3,
                    boxShadow: "0 4px 12px rgba(242,140,40,0.25)",
                    "&:hover": { background: "#e07c24" },
                    "&.Mui-disabled": {
                      background: "#FCD34D",
                      color: "#fff",
                      boxShadow: "none",
                    },
                  }}
                >
                  Publish
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Publish flow — sirf tab jab publish click hua */}
      {click && operationType === OperationType.CREATE && (
        <CreatePublish
          setClick={setClick}
          assessmentDetails={assessmentDetails}
          setAssessmentDetails={setAssessmentDetails}
          questions={questions}
          componentType={componentType}
          redirectStateData={redirectStateData}
          isChecked={isChecked}
          selectedBox={selectedBox}
        />
      )}

      {click && operationType === OperationType.UPDATE && (
        <DraftPublish
          setClick={setClick}
          assessmentDetails={assessmentDetails}
          setAssessmentDetails={setAssessmentDetails}
          questions={questions}
          componentType={componentType}
          isChecked={isChecked}
          selectedBox={selectedBox}
        />
      )}

      {/* Draft processing dialog — sirf heavy case mein */}
      {showDraftProcessing && (
        <Dialog open={true} maxWidth="xs" fullWidth>
          <DialogContent sx={{ textAlign: "center", py: 4 }}>
            <Box sx={{ mb: 2 }}>
              <div className="spinner-border text-primary" />
            </Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Saving as Draft...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait ⏳
              <br />
              This may take a 1-2 minutes due to large number of questions.
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default QuestionListAssessment;
